import React, { useState } from "react";
import Dialog from "@components/Dialog";
import Text from "@components/Text";
import Input from "@components/Input";
import Button from "@components/Button";
import Select from "@components/Select";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import { useLang } from "@src/hooks/useLang";
import { AccessGroup, checkEmail, invite } from "@services/teamService";

interface IProps {
  visible: boolean;
  onClose: () => void;
  onInvited: () => void;
  groups: AccessGroup[];
}

const InviteDialog: React.FC<IProps> = ({ visible, onClose, onInvited, groups }) => {
  const lang = useLang();
  const t = (lang as any).team ?? {};

  const [email, setEmail] = useState("");
  const [groupId, setGroupId] = useState<string>("");
  const [hint, setHint] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);

  const reset = () => {
    setEmail("");
    setGroupId("");
    setHint(null);
  };

  const handleCheck = async () => {
    if (!email.trim()) return;
    setChecking(true);
    setHint(null);
    try {
      const res = await checkEmail(email.trim());
      if (res.isSelf) setHint(t.checkSelf ?? "This is your own account.");
      else if (res.alreadyMember)
        setHint(t.checkAlready ?? "This person already has access.");
      else if (res.exists)
        setHint(t.checkExisting ?? "Existing user — they'll get access immediately.");
      else setHint(t.checkNew ?? "New user — we'll email them a sign-up link.");
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    } finally {
      setChecking(false);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      toast(t.emailRequired ?? "Enter an email", { type: "error" });
      return;
    }
    if (!groupId) {
      toast(t.groupRequired ?? "Choose a group", { type: "error" });
      return;
    }
    setSending(true);
    try {
      const res = await invite(email.trim(), groupId);
      toast(
        res.path === "added"
          ? t.inviteAdded ?? "Access granted and notified by email."
          : `${t.inviteSent ?? "Invitation email sent."} ${
              t.spamHintInvite ??
              "If it doesn't arrive right away, ask them to check their spam folder."
            }`,
        { type: "success" }
      );
      onInvited();
      reset();
      onClose();
    } catch (e: any) {
      toast(e.message ?? e.toString(), { type: "error" });
    } finally {
      setSending(false);
    }
  };

  const options = groups.map((g) => ({ title: g.name, value: g.id }));

  return (
    <Dialog
      style={{ width: 440 }}
      title={t.invitePerson ?? "Invite a person"}
      visible={visible}
      onClose={() => {
        reset();
        onClose();
      }}
    >
      <Column>
        <Row alignItems="flex-end">
          <Input
            label={t.email ?? "Email"}
            placeholder="name@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setHint(null);
            }}
          />
          <SizedBox width={10} />
          <Button
            kind="outline"
            onClick={handleCheck}
            disabled={checking || !email.trim()}
            style={{ width: 110, height: 44 }}
          >
            {t.check ?? "Check"}
          </Button>
        </Row>
        {hint && (
          <>
            <SizedBox height={8} />
            <Text size={13} color="#68AD18">
              {hint}
            </Text>
          </>
        )}

        <SizedBox height={16} />
        <Text size={14} type="heading" color="#494C5B">
          {t.group ?? "Group"}
        </Text>
        <SizedBox height={6} />
        {options.length === 0 ? (
          <Text size={13} color="#899093">
            {t.createGroupFirst ?? "Create a group first."}
          </Text>
        ) : (
          <Select
            options={options}
            value={groupId}
            onChange={setGroupId}
          />
        )}

        <SizedBox height={24} />
        <Row justifyContent="flex-end">
          <Button
            kind="outline"
            onClick={() => {
              reset();
              onClose();
            }}
            style={{ width: 120, marginRight: 12 }}
          >
            {t.cancel ?? "Cancel"}
          </Button>
          <Button
            onClick={handleInvite}
            disabled={sending || options.length === 0}
            style={{ width: 120 }}
          >
            {t.invite ?? "Invite"}
          </Button>
        </Row>
      </Column>
    </Dialog>
  );
};

export default observer(InviteDialog);
